import { authTokenGet, authTokenSet, authTokenIsExpires } from '@codestreet/utils'

import { apiRequest } from './client'
import type { ApiRequestHeaders, ApiRequestOptions, ApiResponseRefreshToken } from './client.types'

const REFRESH_URL = '/refresh'

/**
 * Rich result of a refresh attempt. `requestRefresh` keeps returning a plain
 * boolean for its existing call sites; proactive callers (e.g. token-expiry
 * checks) use `requestRefreshResult` to distinguish a hard 401 rejection
 * (logout) from a transient failure (keep the session).
 */
export type RefreshResult = {
  ok: boolean
  isAuthError: boolean
  responseStatus: number | undefined
}

// Single-flight guard. When the access token expires while several requests are
// in-flight (the common case after ~1h idle), each failed request would fire its
// own POST /refresh. With single-use / rotating refresh tokens the backend
// invalidates the refresh token after the first call, so every other concurrent
// refresh fails — which previously surfaced to the user as a spurious logout.
// Sharing one in-flight refresh promise collapses those into a single call.
// Both the reactive (createApiClient) and proactive (token-expiry) paths go
// through here, so they share one guard.
let inFlightRefresh: Promise<RefreshResult> | null = null

export function requestRefreshResult(
  authURL: string,
  headers: ApiRequestHeaders,
  options?: ApiRequestOptions,
): Promise<RefreshResult> {
  if (inFlightRefresh) return inFlightRefresh

  inFlightRefresh = refreshOnce(authURL, headers, options).finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}

export function requestRefresh(
  authURL: string,
  headers: ApiRequestHeaders,
  options?: ApiRequestOptions,
): Promise<boolean> {
  return requestRefreshResult(authURL, headers, options).then((result) => result.ok)
}

async function refreshOnce(
  authURL: string,
  headers: ApiRequestHeaders,
  options?: ApiRequestOptions,
): Promise<RefreshResult> {
  const token = authTokenGet()

  if (!token) {
    return { ok: false, isAuthError: false, responseStatus: undefined }
  }

  const formData = new FormData()
  formData.append('access_token', token.accessToken)
  formData.append('refresh_token', token.refreshToken)

  const requestURL = new URL(authURL + REFRESH_URL)

  const response = await apiRequest<ApiResponseRefreshToken>(
    requestURL,
    'POST',
    formData,
    headers,
    options,
  )

  if (response.isOk && response.data) {
    const { accessToken, refreshToken } = response.data

    authTokenSet({
      accessToken,
      refreshToken,
      expiresAt: response.data.expires,
    })

    return {
      ok: true,
      isAuthError: false,
      responseStatus: response.responseStatus,
    }
  }

  // The refresh call failed. Before reporting failure (which makes the caller
  // give up and can trigger a logout), check whether a concurrent refresh —
  // another code path (e.g. proactive token check) or another browser tab —
  // already stored a fresh, non-expired token. If so the session is still
  // valid and the caller should retry with the new token.
  if (!authTokenIsExpires()) {
    return {
      ok: true,
      isAuthError: response.isAuthError,
      responseStatus: response.responseStatus,
    }
  }

  return {
    ok: false,
    isAuthError: response.isAuthError,
    responseStatus: response.responseStatus,
  }
}
